import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { FormFilter } from "@features/forms/components/form-filter/form-filter";
import { EmissionSourceApi } from '@features/forms/services/emission-source-api';
import { EmissionSourceFormulaModel, EmissionSourceHierarchyFilterModel, EmissionSourceHierarchyResponseModel, FormulaSearchModel, EmissionSourceExcelTemplateRequestModel } from '@features/forms/models/emission-source.model';
import { DataEndpoint } from '@core/models/general.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import * as XLSX from 'xlsx';
import { MessageService } from 'primeng/api';
import { Toast } from "primeng/toast";
import { BusinessStateService } from '@core/services/business-state.service';
import { Observable, Subject, firstValueFrom, forkJoin, takeUntil, delay, concatMap, of } from 'rxjs';
import { ResultsApi } from '@features/results/services/results-api';
import { ReportResultGenerateRequest } from '@features/reports/models/results.model';
import { skip, distinctUntilChanged, catchError } from 'rxjs/operators';
import { GlobalWarmingPotentialApi } from '@features/forms/services/global-warming-potential-api';
import { GlobalWarmingPotentialModel } from '@features/forms/models/global-warming-potential.model';
import { FormBuilder } from "@features/forms/components/form-builder/form-builder";
import { EmissionsFactoryApi } from '@features/emissions/services/emissions-factory-api';
import { EmissionsFactoryForFormFilterModel, EmissionsFactoryForFormModel } from '@features/emissions/models/emissions-factory.model';
import { ConversionApi } from '@features/forms/services/conversion-api';
import { ConversionModel } from '@features/forms/models/conversion.model';
import { ActivitiesApi } from '@features/forms/services/activities-api';
import { ActivitiesFilterModel, ActivitiesModel, ActivitiesRowModel, ActivitiesPostModel, ActivitiesAddModel, ActivitiesUpdateModel, ActivitiesFilesPostModel, ActivityFilesModel } from '@features/forms/models/activities.model';
import { FormFooter } from "@shared/components/form-footer/form-footer";
import { Dialog } from "primeng/dialog";
import { FileUpload } from "primeng/fileupload";
import { ConfirmationDialog } from "@shared/components/confirmation-dialog/confirmation-dialog";
import { UnsavedChangesService } from '@features/forms/services/unsaved-changes.service';

@Component({
  selector: 'app-forms',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    BlockUI,
    ProgressSpinner,
    FormFilter,
    Toast,
    FormBuilder,
    FormFooter,
    Dialog,
    FileUpload,
    ConfirmationDialog
  ],
  templateUrl: './forms.html',
  styleUrl: './forms.scss',
  providers: [MessageService]
})
export class Forms implements OnInit, OnDestroy {
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  @ViewChild(FormFilter) formFilter!: FormFilter;
  @ViewChild(FormBuilder) formBuilder!: FormBuilder;

  private readonly messageService = inject(MessageService);
  private readonly businessStateService = inject(BusinessStateService);
  private readonly emissionSourceApi = inject(EmissionSourceApi);
  private readonly activitiesApi = inject(ActivitiesApi);
  private readonly emissionsFactoryApi = inject(EmissionsFactoryApi);
  private readonly globalWarmingPotentialApi = inject(GlobalWarmingPotentialApi);
  private readonly conversionApi = inject(ConversionApi);
  private readonly resultsApi = inject(ResultsApi);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly http = inject(HttpClient);
  private readonly destroy$ = new Subject<void>();

  officeId: string | null = null;
  visibleConfirmModal: boolean = false;

  isLoading: boolean = false;
  isDownloadingExcel: boolean = false;
  private factorsMap: Record<string, string> = {};

  get isDownloadDisabled(): boolean {
    return this.isLoading || !this.activities?.Rows || this.activities.Rows.length === 0;
  }
  formula: EmissionSourceFormulaModel = {} as EmissionSourceFormulaModel;
  activities: ActivitiesModel = {} as ActivitiesModel;
  emissionFactorOptions: EmissionsFactoryForFormModel[] = [];
  globalWarmingPotentialOptions: GlobalWarmingPotentialModel[] = [];
  conversionOptions: ConversionModel[] = [];
  filters: FormulaSearchModel = {} as FormulaSearchModel;

  emissionSourceHierarchy: EmissionSourceHierarchyResponseModel | null = null;

  // Almacenar los datos editados de la tabla
  editedTableData: any[] = [];

  // Detectar si hay actividades nuevas agregadas
  hasNewActivity: boolean = false;

  visibleUploadFilesDialog: boolean = false;
  selectedActivity: ActivitiesRowModel = {} as ActivitiesRowModel;
  uploadedFiles: ActivityFilesModel[] = [];

  visibleDeleteDialog: boolean = false;
  activityToDelete: ActivitiesRowModel | null = null;
  activitiesDelete: string[] = [];

  mode: 'none' | 'manual' | 'excel' = 'none';
  templateStatus: 'idle' | 'success' | 'error' | 'not-found' = 'idle';
  excelFile: File | null = null;
  excelTemplateUrl: string | null = null;

  ngOnInit() {
    this.unsavedChangesService.requestConfirm$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.visibleConfirmModal = true;
      });

    const office = this.businessStateService.getSelectedBusiness();
    this.officeId = office?.data?.id;

    this.getEmissionSourceHierarchy();
    this.callDataFromStorage();

    // Obtener cambios en la sede seleccionada
    this.businessStateService.selectedBusiness$
      .pipe(
        takeUntil(this.destroy$),
        skip(1), // Evita duplicación en la consulta
        distinctUntilChanged((prev: any, curr: any) => {
          return prev?.data?.id === curr?.data?.id;
        }) // Solo ejecutar si la sede cambió realmente
      )
      .subscribe((curr: any) => {
        this.officeId = curr?.data?.id;
        this.clearAllData();
        this.getEmissionSourceHierarchy();
      });
  }

  callDataFromStorage() {
    const gwpData = localStorage.getItem('globalWarmingPotential');
    if (gwpData) {
      this.globalWarmingPotentialOptions = JSON.parse(gwpData);
    }
    else {
      this.getGlobalWarmingPotential();
    }

    const conversionData = localStorage.getItem('conversion');
    if (conversionData) {
      this.conversionOptions = JSON.parse(conversionData);
    }
    else {
      this.getConversion();
    }
  }

  getEmissionSourceHierarchy() {
    if (this.officeId) {
      this.isLoading = true;

      const request: EmissionSourceHierarchyFilterModel = {
        officeId: this.officeId
      }

      this.emissionSourceApi.getEmissionSourceHierarchy(request).subscribe({
        next: (response: DataEndpoint<EmissionSourceHierarchyResponseModel>) => {
          if (response.Status === 200) {
            this.emissionSourceHierarchy = response.Data!;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
          });
        }
      })
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se ha seleccionado una oficina. Por favor, seleccione una oficina para continuar.'
      });
    }
  }

  getGlobalWarmingPotential() {
    this.isLoading = true;

    this.globalWarmingPotentialApi.getGlobalWarmingPotential().subscribe({
      next: (response: DataEndpoint<GlobalWarmingPotentialModel[]>) => {
        if (response.Status === 200) {
          this.globalWarmingPotentialOptions = response.Data!;
          localStorage.setItem('globalWarmingPotential', JSON.stringify(this.globalWarmingPotentialOptions));
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
        });
      }
    })
  }

  getConversion() {
    this.isLoading = true;

    this.conversionApi.getConversion().subscribe({
      next: (response: DataEndpoint<ConversionModel[]>) => {
        if (response.Status === 200) {
          this.conversionOptions = response.Data!;
          localStorage.setItem('conversion', JSON.stringify(this.conversionOptions));
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
        });
      }
    })
  }

  getEmissionSourceFormula(emissionSourceId: string, filters?: FormulaSearchModel) {
    this.isLoading = true;

    this.emissionSourceApi.getEmissionSourceFormulasById(emissionSourceId).subscribe({
      next: (response: DataEndpoint<EmissionSourceFormulaModel>) => {
        if (response.Status === 200) {
          const fetchedFormula = response.Data!;

          if (fetchedFormula.ActivityDefinition) {
            fetchedFormula.ActivityDefinition.forEach(def => {
              if (def.Code === 'UNIT_WTT') {
                def.Code = 'UNIT';
              }
            });
          }

          this.formula = fetchedFormula;

          if (this.officeId && filters?.year) {
            // Llamar listado de actividades
            this.getActivities(filters);

            const emissionFactorDef = this.formula.ActivityDefinition?.find(def => def.Type === 'emission_factor');
            if (emissionFactorDef?.Items && Array.isArray(emissionFactorDef.Items)) {
              this.getEmissionFactorForForm(emissionFactorDef.Items, filters);
            }

          }
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
        });
      }
    })
  }

  getActivities(filters: FormulaSearchModel) {
    const request: ActivitiesFilterModel = {
      year: filters.year,
      officeId: this.officeId!,
      sourceId: filters.emissionSource
    };

    this.isLoading = true;

    this.activitiesApi.getActivities(request).subscribe({
      next: (resp: DataEndpoint<ActivitiesModel>) => {
        const response: any = resp;
        if (response.status === 200) {
          const fetchedActivities = response.data;

          // Reemplazar UNIT_WTT con UNIT
          if (fetchedActivities.Rows) {
            fetchedActivities.Rows.forEach((row: any) => {
              if (row.Definition && 'UNIT_WTT' in row.Definition) {
                row.Definition['UNIT'] = row.Definition['UNIT_WTT'];
                delete row.Definition['UNIT_WTT'];
              }
            });
          }

          this.activities = fetchedActivities;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
        });
      }
    })
  }

  getEmissionFactorForForm(items: string[], filters: FormulaSearchModel) {
    const request: EmissionsFactoryForFormFilterModel = {
      codes: items.join(','),
      year: filters.year,
      officeId: this.officeId!,
      variation: filters.variation
    };

    this.isLoading = true;
    this.emissionsFactoryApi.getEmissionFactorForForm(request).subscribe({
      next: (response: DataEndpoint<EmissionsFactoryForFormModel[]>) => {
        if (response.Status === 200) {
          this.emissionFactorOptions = response.Data!;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error retornando la información. Por favor, inténtelo nuevamente.'
        });
      }
    })
  }

  onSearch(filters: FormulaSearchModel) {
    this.filters = filters;
    // Resetear los datos editados cuando se hace una nueva búsqueda
    this.editedTableData = [];
    // Resetear el flag de nueva actividad y eliminadas
    this.hasNewActivity = false;
    this.activitiesDelete = [];
    this.unsavedChangesService.setUnsavedChanges(false);

    this.mode = 'none';
    this.templateStatus = 'idle';
    this.excelFile = null;
    this.excelTemplateUrl = null;

    if (filters.emissionSource && this.officeId) {
      this.isLoading = true;
      const request: EmissionSourceExcelTemplateRequestModel = {
        officeId: this.officeId,
        year: filters.year,
        gwp: filters.pcg
      };

      this.emissionSourceApi.getEmissionSourceExcelTemplate(filters.emissionSource, request)
        .subscribe({
          next: (fileUrl: string) => {
            this.mode = 'excel';
            this.templateStatus = 'success';
            this.excelTemplateUrl = fileUrl;

            this.isLoading = false;
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 400) {
              this.mode = 'manual';
              this.getEmissionSourceFormula(filters.emissionSource, filters);
            } else if (error.status === 404) {
              this.mode = 'excel';
              this.templateStatus = 'not-found';
              this.isLoading = false;
            } else {
              this.mode = 'excel';
              this.templateStatus = 'error';
              this.isLoading = false;
            }
          }
        });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Todos los campos son obligatorios.'
      });
    }
  }

  onGwpChange(gwpId: string): void {
    // Sincronizar los datos editados de la tabla antes de cambiar el GWP para no perder cambios locales
    if (this.formBuilder) {
      this.formBuilder.stopEditing();
    }
    this.syncTableDataToActivities();

    // Re-asignar para asegurar que el signal input en el hijo se actualice con los datos sincronizados
    this.activities = { ...this.activities };

    // Actualizar el filtro con el nuevo GWP
    if (this.filters) {
      this.filters = {
        ...this.filters,
        pcg: gwpId
      };
    }
  }

  /**
   * Valida que los campos requeridos en ActivityDefinition y MeasurementDefinition tengan valores
   */
  private validateRequiredFields(): string | null {
    if (!this.formula.ActivityDefinition?.length && !this.formula.MeasurementDefinition?.length) {
      return null;
    }

    const missingFields: string[] = [];

    // Usar editedTableData si existe, de lo contrario usar activities.Rows
    const dataToValidate = this.editedTableData.length > 0 ? this.editedTableData :
      this.activities.Rows?.map(row => row.Definition) || [];

    dataToValidate.forEach((rowData: any, rowIndex: number) => {
      // Validar campos de ActivityDefinition
      this.formula.ActivityDefinition?.forEach(def => {
        if (def.Required) {
          const value = rowData[def.Code];
          if (!value) {
            missingFields.push(`Fila ${rowIndex + 1}: ${def.Code}`);
          }
        }
      });

      // Validar campos de MeasurementDefinition
      this.formula.MeasurementDefinition?.forEach(def => {
        if (def.Required) {
          const value = rowData[def.Code];
          if (!value) {
            missingFields.push(`Fila ${rowIndex + 1}: ${def.Code}`);
          }
        }
      });
    });

    if (missingFields.length > 0) {
      return `Los siguientes campos obligatorios están vacíos:\n${missingFields.join('\n')}`;
    }

    return null;
  }

  /**
   * Sincroniza los datos editados de la tabla de vuelta a las activities
   */
  private syncTableDataToActivities() {
    if (!this.editedTableData || this.editedTableData.length === 0) return;

    // Obtener los códigos de las columnas de MeasurementDefinition para diferenciarlos
    const measurementCodes = new Set<string>();
    if (this.formula?.MeasurementDefinition) {
      this.formula.MeasurementDefinition.forEach(def => measurementCodes.add(def.Code));
    }

    // Actualizar las filas existentes con los datos de la tabla
    if (this.activities.Rows) {
      this.activities.Rows.forEach((activityRow, index) => {
        if (this.editedTableData[index]) {
          const tableRow = this.editedTableData[index];

          if (!activityRow.Definition) activityRow.Definition = {} as any;
          if (!activityRow.Measurements) activityRow.Measurements = { Id: '' } as any;

          // Actualizar los campos específicos de la definición con los datos de la tabla
          if (tableRow.NAME !== undefined) activityRow.Definition['NAME'] = tableRow.NAME;
          if (tableRow.FACTOR !== undefined) activityRow.Definition['FACTOR'] = tableRow.FACTOR;
          if (tableRow.UNIT !== undefined) activityRow.Definition['UNIT'] = tableRow.UNIT;

          // Actualizar otros campos dinámicos que puedan existir en la tabla
          Object.keys(tableRow).forEach(key => {
            if (!['NAME', 'FACTOR', 'UNIT'].includes(key) && tableRow[key] !== undefined) {
              // Si la columna es de MeasurementDefinition, guardarla en Measurements, sino en Definition
              if (measurementCodes.has(key)) {
                (activityRow.Measurements as any)[key] = tableRow[key];
              } else {
                (activityRow.Definition as any)[key] = tableRow[key];
              }
            }
          });
        }
      });
    }

    // Limpiar editedTableData para evitar que aplique datos obsoletos si la cantidad de filas cambia (ej: eliminar/agregar fila)
    this.editedTableData = [];
  }

  /**
   * Maneja los cambios en los datos de la tabla
   */
  onTableDataChange(tableData: any[]) {
    this.editedTableData = [...tableData];

    // Detectar si hay nuevas actividades (filas con ID temporal)
    const hasNewRows = this.activities.Rows?.some(row => row.Id.startsWith('temp-')) || false;

    // Detectar si hay cambios en los datos editados comparando con los datos originales
    const hasChanges = this.detectDataChanges(tableData);

    // Activar el botón de guardar si hay nuevas filas o cambios en los datos
    this.hasNewActivity = hasNewRows || hasChanges;
    this.unsavedChangesService.setUnsavedChanges(this.hasNewActivity);
  }

  private detectDataChanges(editedData: any[]): boolean {
    if (!editedData || editedData.length === 0) {
      return false;
    }

    if (!this.activities.Rows || this.activities.Rows.length === 0) {
      return false;
    }

    // Obtener códigos de campos editables (ActivityDefinition y MeasurementDefinition)
    const editableFields = new Set<string>();
    this.formula.ActivityDefinition?.forEach(def => editableFields.add(def.Code));
    this.formula.MeasurementDefinition?.forEach(def => editableFields.add(def.Code));

    // Comparar cada fila editada con su correspondiente en activities
    for (let i = 0; i < editedData.length; i++) {
      const editedRow = editedData[i];
      const originalRow = this.activities.Rows[i];

      if (!originalRow) {
        // Si hay más filas editadas que originales, hay cambios
        return true;
      }

      // Comparar solo campos editables
      for (const key of editableFields) {
        const editedValue = editedRow[key];

        // Obtener valor original de Definition o Measurements
        let originalValue: any;
        if (this.formula.ActivityDefinition?.some(def => def.Code === key)) {
          originalValue = originalRow.Definition?.[key as keyof typeof originalRow.Definition];
        } else if (this.formula.MeasurementDefinition?.some(def => def.Code === key)) {
          originalValue = (originalRow.Measurements as any)?.[key];
        }

        // Comparar valores (considerando conversión de tipos y null/undefined)
        const editedStr = editedValue !== undefined && editedValue !== null ? String(editedValue) : '';
        const originalStr = originalValue !== undefined && originalValue !== null ? String(originalValue) : '';

        if (editedStr !== originalStr) {
          return true;
        }
      }
    }

    return false;
  }

  onAddActivity() {
    if (!this.activities.OfficeId || !this.activities.SourceId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Debe realizar una búsqueda primero antes de agregar actividades.'
      });
      return;
    }

    this.formBuilder?.stopEditing();
    // Sincronizar los datos editados de la tabla antes de agregar nueva fila
    this.syncTableDataToActivities();

    let currentRowNumber = 0;
    if (this.activities.Rows?.length > 0) {
      currentRowNumber = this.activities.Rows[this.activities.Rows.length - 1].Order + 1;
    }

    // Crear una nueva fila de actividad
    const newRow: ActivitiesRowModel = {
      Id: `temp-${Date.now()}`, // ID temporal único
      Order: currentRowNumber,
      Definition: {
        NAME: '',
        FACTOR: '',
        UNIT: ''
      },
      Measurements: {
        Id: ''
      }
    };

    // Agregar la nueva fila al array de activities
    this.activities = {
      ...this.activities,
      Rows: [...(this.activities.Rows || []), newRow]
    };

    // Indicar que hay una nueva actividad
    this.hasNewActivity = true;
    this.unsavedChangesService.setUnsavedChanges(true);
  }

  /**
   * Ejecuta un array de observables de forma secuencial con un delay entre cada uno
   * para evitar condiciones de carrera
   */
  private executeSequentialRequests(requests: Observable<DataEndpoint<ActivitiesPostModel[]>>[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let index = 0;

      const executeNext = () => {
        if (index >= requests.length) {
          resolve();
          return;
        }

        const currentRequest = requests[index];
        index++;

        // Agregar delay antes de cada request excepto el primero
        const delayMs = index > 1 ? 300 : 0;

        setTimeout(() => {
          firstValueFrom(currentRequest).then(
            () => executeNext(),
            (error) => reject(error)
          );
        }, delayMs);
      };

      executeNext();
    });
  }

  async onSave() {
    if (!this.officeId || !this.activities.SourceId || !this.activities.Year) {
      this.messageService.add({
        severity: 'error',
        summary: 'Acción fallida',
        detail: 'Los campos de búsqueda son obligatorios.'
      });
      return;
    }

    this.formBuilder?.stopEditing();
    this.syncTableDataToActivities();

    const validationError = this.validateRequiredFields();

    if (validationError) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: validationError
      });
      return;
    }

    this.isLoading = true;

    try {

      await this.executeSequentialRequests(this.getRequestsByGwp());

      // Generar resultados después de guardar las actividades
      const generateRequest: ReportResultGenerateRequest = {
        SourceId: this.filters.emissionSource,
        OfficeId: this.officeId!,
        Year: this.filters.year
      };
      await firstValueFrom(this.resultsApi.postGenerate(generateRequest));

      this.messageService.add({
        severity: 'success',
        summary: 'Acción exitosa',
        detail: 'Las actividades se guardaron correctamente.'
      });

      // Resetear el flag de nueva actividad y lista de eliminadas
      this.hasNewActivity = false;
      this.activitiesDelete = [];
      this.unsavedChangesService.setUnsavedChanges(false);
      this.getActivities(this.filters);

    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.Message ?? 'Ha ocurrido un error al guardar las actividades. Por favor, inténtelo nuevamente.'
      });
    } finally {
      this.isLoading = false;
    }
  }

  onDownloadTemplate() {
    if (this.excelTemplateUrl) {
      const a = document.createElement('a');
      a.href = this.excelTemplateUrl;
      a.target = '_blank';
      a.download = 'plantilla_fuente_emision.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  onUploadExcelTemplate(file: File) {
    this.excelFile = file;
    this.hasNewActivity = true;
    this.unsavedChangesService.setUnsavedChanges(true);
  }

  onSaveExcel() {
    if (!this.officeId || !this.filters.year || !this.filters.pcg || !this.excelFile) {
      return;
    }

    this.isLoading = true;
    const formData = new FormData();
    formData.append('officeId', this.officeId);
    formData.append('sourceId', this.filters.emissionSource);
    formData.append('year', this.filters.year.toString());
    formData.append('gwp', this.filters.pcg);
    formData.append('file', this.excelFile);

    this.emissionSourceApi.uploadEmissionSourceExcel(formData).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Status === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'El archivo Excel se cargó correctamente.'
          });
          this.excelFile = null;
          this.hasNewActivity = false;
          this.unsavedChangesService.setUnsavedChanges(false);
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error al cargar el archivo. Por favor, inténtelo nuevamente.'
        });
      }
    });
  }

  getRequestsByGwp(): Observable<DataEndpoint<ActivitiesPostModel[]>>[] {
    const originalPcg = this.filters.pcg;
    const selectedGwp = this.globalWarmingPotentialOptions.find((el) => el.Id === this.filters.pcg);

    // Obtener ítems GWP no seleccionados
    const remainingGwps = this.globalWarmingPotentialOptions.filter(reference => reference.Reference !== selectedGwp?.Reference);

    // Conformar array con todos los ítems de GWP
    const allGwps = [selectedGwp, ...remainingGwps];

    const requests: Observable<DataEndpoint<ActivitiesPostModel[]>>[] = [];

    for (const gwp of allGwps) {

      let rows: ActivitiesRowModel[] | undefined = []

      const calculatedRows = this.formBuilder.forceCalculateAllRows(gwp?.Id);

      rows = calculatedRows?.map(r => ({
        ...r,
        Definition: { ...(r.Definition || {}) },
        Measurements: { ...(r.Measurements || {}) }
      }));

      const added: ActivitiesAddModel[] = [];
      const updated: ActivitiesUpdateModel[] = [];

      // Obtener las columnas ordenadas para garantizar el registro en el mismo orden de la tabla
      const activityCodes = this.formula.ActivityDefinition?.map(d => d.Code) || [];
      const measurementCodes = this.formula.MeasurementDefinition?.map(d => d.Code) || [];
      const resultCodes = this.formula.ResultDefinition?.map(d => d.Code) || [];

      const measurementCodesSet = new Set(measurementCodes);

      const orderedCodes = [
        ...activityCodes,
        ...measurementCodes,
        ...resultCodes
      ];

      rows?.forEach(row => {

        const fields: { [key: string]: string | number } = {};

        orderedCodes.forEach(code => {
          let value: any = "";
          if (measurementCodesSet.has(code)) {
            value = (row.Measurements as any)?.[code] ?? "";
          } else {
            value = (row.Definition as any)?.[code] ?? "";
          }
          // Agregar prefijo -ME para columnas de MeasurementCodes
          const fieldKey = measurementCodesSet.has(code) ? `ME-${code}` : code;
          fields[fieldKey] = String(value);

          if (code === 'UNIT') {
            fields['UNIT_WTT'] = String(value);
          }
        });

        if (row.Id.startsWith('temp-')) {
          added.push({
            Order: row.Order,
            Fields: fields
          });
        } else {
          updated.push({
            Id: row.Id,
            Order: row.Order,
            Fields: fields
          });
        }
      });

      const request: ActivitiesPostModel = {
        OfficeId: this.activities.OfficeId,
        SourceId: this.activities.SourceId,
        Year: this.activities.Year,
        Added: added,
        Updated: updated,
        GWP: gwp?.Reference
      };

      if (this.activitiesDelete.length > 0) {
        request.Deleted = [...this.activitiesDelete];
      }

      requests.push(this.activitiesApi.postActivities(request));
    }

    // Restaurar el PCG original en los filtros y forzar el recalculo para dejar la tabla en el estado correcto visualmente
    this.filters.pcg = originalPcg;
    this.formBuilder.forceCalculateAllRows(originalPcg);

    return requests;
  }

  onOpenUploadFilesDialog({ activity, open }: { activity: ActivitiesRowModel, open: boolean }) {
    if (open && activity?.Id) {
      this.selectedActivity = activity;
      this.getActivityFiles();
      this.visibleUploadFilesDialog = true;
    }
  }

  resetFileUpload() {
    this.fileUpload?.clear();
  }

  onCloseUploadFiles() {
    this.visibleUploadFilesDialog = false;
  }

  onDeleteActivity(activity: ActivitiesRowModel) {
    this.activityToDelete = activity;
    this.visibleDeleteDialog = true;
  }

  confirmDelete() {
    if (this.activityToDelete) {
      this.formBuilder?.stopEditing();
      // Sincronizar los datos editados antes de eliminar para no perder los cambios de las otras filas
      this.syncTableDataToActivities();

      const id = this.activityToDelete.Id;

      if (id.startsWith('temp-')) {
        this.activities.Rows = this.activities.Rows?.filter(r => r.Id !== id) || [];
      } else {
        this.activitiesDelete.push(id);
        this.activities.Rows = this.activities.Rows?.filter(r => r.Id !== id) || [];
      }

      this.activities = {
        ...this.activities,
        Rows: this.activities.Rows
      };

      this.hasNewActivity = true;
      this.unsavedChangesService.setUnsavedChanges(true);
      this.visibleDeleteDialog = false;
      this.activityToDelete = null;
    }
  }

  cancelDelete() {
    this.visibleDeleteDialog = false;
    this.activityToDelete = null;
  }

  getActivityFiles() {
    this.activitiesApi.getActivityFiles(this.selectedActivity.Id).subscribe({
      next: (response: DataEndpoint<ActivityFilesModel[]>) => {
        if (response.Status === 200) {
          this.uploadedFiles = response.Data!;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Ha ocurrido un error recuperando los adjuntos. Por favor, inténtelo nuevamente.'
        });
      }
    });
  }

  async downloadExistingFile(file: ActivityFilesModel) {
    if (file.PresignedUrl) {
      const a = document.createElement('a');
      a.href = file.PresignedUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.click();
    }
  }

  downloadNewFile(file: File) {
    const objectUrl = URL.createObjectURL(file);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = file.name;
    a.click();

    URL.revokeObjectURL(objectUrl);
  }

  onSelectFile(event: any) {
    if (event.files.length !== this.fileUpload.files.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Archivos inválidos',
        detail: 'Formatos permitidos: XLSX, PDF, JPG, PNG. Tamaño máximo por archivo: 10MB.'
      });
    }
  }

  onSaveFiles() {
    if (this.selectedActivity?.Id && this.validFiles.length > 0) {
      const request: ActivitiesFilesPostModel = {
        year: this.filters.year,
        files: this.validFiles
      };

      const formData: FormData = new FormData();
      formData.append('year', request.year.toString());
      request.files.forEach((file) => {
        formData.append('files', file);
      });

      this.isLoading = true;

      this.activitiesApi.postActivityFiles(this.selectedActivity.Id, formData).subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Status === 200) {
            this.messageService.add({
              severity: 'success',
              summary: 'Acción exitosa',
              detail: 'Los archivos se guardaron correctamente.'
            });
            this.visibleUploadFilesDialog = false;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.Message ?? 'Ha ocurrido un error guardando los adjuntos. Por favor, inténtelo nuevamente.'
          });
        }
      });
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error guardando los adjuntos. Por favor, inténtelo nuevamente.'
      });
    }
  }

  get validFiles(): File[] {
    return this.fileUpload?.files ?? [];
  }

  removeFile(file: File) {
    const index = this.fileUpload.files.indexOf(file);

    if (index !== -1) {
      this.fileUpload.files.splice(index, 1);
    }
  }

  /**
   * Limpia todos los datos de la tabla y filtros cuando cambia la sede
   */
  clearAllData(): void {
    // Limpiar datos de la tabla
    this.activities = {} as ActivitiesModel;
    this.editedTableData = [];
    this.hasNewActivity = false;
    this.activitiesDelete = [];

    // Limpiar fórmula y factores de emisión
    this.formula = {} as EmissionSourceFormulaModel;
    this.emissionFactorOptions = [];

    // Limpiar filtros
    this.filters = {} as FormulaSearchModel;

    // Resetear filtros del componente form-filter
    if (this.formFilter) {
      this.formFilter.resetFilters();
    }

    this.mode = 'none';
    this.templateStatus = 'idle';
    this.excelFile = null;
    this.excelTemplateUrl = null;
  }

  onCloseModal(confirm: boolean) {
    this.visibleConfirmModal = false;
    if (confirm) {
      this.hasNewActivity = false;
    }
    this.unsavedChangesService.resolveConfirm(confirm);
  }

  ngOnDestroy() {
    this.unsavedChangesService.setUnsavedChanges(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onDownloadExcel(): Promise<void> {
    if (this.isDownloadDisabled || this.isDownloadingExcel) return;

    this.isDownloadingExcel = true;

    try {
      if (Object.keys(this.factorsMap).length === 0) {
        this.factorsMap = await firstValueFrom(
          this.http.get<Record<string, string>>('assets/data/factors.json').pipe(
            catchError(() => of({}))
          )
        );
      }

      const gridApi = this.formBuilder.agGrid.api;
      const colDefs = this.formBuilder.columnDefs;
      const excelData: any[] = [];

      this.formBuilder.stopEditing();

      gridApi.forEachNodeAfterFilterAndSort((node) => {
        const rowObj: any = {};

        colDefs.forEach(colDef => {
          if (colDef.field) {
            const rawHeader = colDef.headerName || colDef.field || '';
            const header = rawHeader.replace(/<[^>]*>?/gm, '');
            let value = node.data[colDef.field];

            if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function') {
              const formatted = colDef.valueFormatter({
                value,
                data: node.data,
                node,
                colDef,
                api: gridApi
              } as any);

              if (formatted !== undefined && formatted !== null) {
                value = formatted;
              }
            }

            if (colDef.type === 'emission_factor' || colDef.field === 'FACTOR') {
              const factorOption = this.emissionFactorOptions.find(opt => opt.Id === value);
              if (factorOption && factorOption.Code) {
                value = this.factorsMap[factorOption.Code] || factorOption.Code;
              }
            }

            // Forzar a string usando Intl.NumberFormat para formatear con punto de miles 
            // y coma decimal (ej. 1.017,18).
            if (typeof value === 'number') {
              value = new Intl.NumberFormat('es-CO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 20 // Se usa 20 (el máximo permitido) para que no corte los decimales
              }).format(value);
            } else if (colDef.field && colDef.field.startsWith('MONTH-') && typeof value === 'string' && value.trim() !== '') {
              // Algunos valores editados o crudos de columnas MeasurementDefinition pueden ser strings
              const num = Number(value);
              if (!isNaN(num)) {
                value = new Intl.NumberFormat('es-CO', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 20
                }).format(num);
              }
            } else if (typeof value === 'string' && value.endsWith(' %')) {
              // Aplicar el mismo formato a los porcentajes devueltos por el valueFormatter
              const numStr = value.replace(' %', '');
              const num = Number(numStr);
              if (!isNaN(num)) {
                const formattedNum = new Intl.NumberFormat('es-CO', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 20
                }).format(num);
                value = `${formattedNum} %`;
              }
            }

            rowObj[header] = value;
          }
        });
        excelData.push(rowObj);
      });

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
      const workbook: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
      const fileName = 'Formularios.xlsx';

      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un problema exportando el archivo. Por favor intente nuevamente.'
      });
    } finally {
      this.isDownloadingExcel = false;
    }
  }
}
